import {
  collection,
  deleteField,
  doc,
  getDoc,
  getDocs,
  limit,
  onSnapshot,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
} from "firebase/firestore";
import { db } from "./firebaseConfig";
import {
  normalizeStudentUid,
  shouldActivateQrForStudentName,
} from "../utils/studentQr";

const STUDENTS_COLLECTION = "students";
const studentsCollectionReference = collection(db, STUDENTS_COLLECTION);

export class StudentQrError extends Error {
  constructor(code, message, details = {}) {
    super(message);
    this.name = "StudentQrError";
    this.code = code;
    this.details = details;
  }
}

function mapStudentDocument(documentSnapshot) {
  return {
    id: documentSnapshot.id,
    ...documentSnapshot.data(),
  };
}

export function subscribeToStudents(onNext, onError) {
  return onSnapshot(
    studentsCollectionReference,
    (snapshot) => {
      const students = snapshot.docs
        .map(mapStudentDocument)
        .sort((left, right) =>
          String(left.uid || "").localeCompare(String(right.uid || "")),
        );

      onNext(students);
    },
    onError,
  );
}

export async function getStudentByUid(uid) {
  const normalizedUid = normalizeStudentUid(uid);

  if (!normalizedUid) {
    throw new StudentQrError("invalid-uid", "Invalid student UID.");
  }

  const studentQuery = query(
    studentsCollectionReference,
    where("uid", "==", normalizedUid),
    limit(1),
  );

  const snapshot = await getDocs(studentQuery);

  if (snapshot.empty) {
    throw new StudentQrError("student-not-found", "Student not found", {
      uid: normalizedUid,
    });
  }

  return mapStudentDocument(snapshot.docs[0]);
}

export async function findStudentByUid(uid) {
  try {
    return await getStudentByUid(uid);
  } catch (error) {
    if (error instanceof StudentQrError && error.code === "student-not-found") {
      return null;
    }
    throw error;
  }
}

export async function upsertInactiveStudentQr({
  uid,
  qrImageUrl,
  academicYear = "",
  existingStudent = null,
}) {
  const normalizedUid = normalizeStudentUid(uid);
  const normalizedAcademicYear = String(academicYear || "").trim();

  if (!normalizedUid) {
    throw new StudentQrError("invalid-uid", "Invalid student UID.");
  }

  if (!String(qrImageUrl || "").trim()) {
    throw new StudentQrError(
      "missing-qr-image-url",
      "Cannot upsert student QR without a QR image URL.",
    );
  }

  const updatePayload = {
    uid: normalizedUid,
    qrImageUrl,
    activeqr: false,
    updatedAt: serverTimestamp(),
    ...(normalizedAcademicYear
      ? { admissionYear: normalizedAcademicYear }
      : {}),
  };

  const resolvedStudent =
    existingStudent || (await findStudentByUid(normalizedUid));

  if (resolvedStudent?.id) {
    await updateDoc(
      doc(db, STUDENTS_COLLECTION, resolvedStudent.id),
      updatePayload,
    );
    return {
      created: false,
      studentId: resolvedStudent.id,
      uid: normalizedUid,
    };
  }

  const studentDocumentReference = doc(db, STUDENTS_COLLECTION, normalizedUid);
  const studentDocumentSnapshot = await getDoc(studentDocumentReference);

  if (studentDocumentSnapshot.exists()) {
    await updateDoc(studentDocumentReference, updatePayload);
    return {
      created: false,
      studentId: normalizedUid,
      uid: normalizedUid,
    };
  }

  await setDoc(studentDocumentReference, {
    uid: normalizedUid,
    name: "",
    className: "",
    phone: "",
    qrImageUrl,
    activeqr: false,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    ...(normalizedAcademicYear
      ? { admissionYear: normalizedAcademicYear }
      : {}),
  });

  return {
    created: true,
    studentId: normalizedUid,
    uid: normalizedUid,
  };
}

export async function getStudentByIdentifier(identifier) {
  const rawIdentifier = String(identifier || "").trim();
  const normalizedIdentifier = normalizeStudentUid(identifier);

  if (!normalizedIdentifier) {
    throw new StudentQrError(
      "invalid-identifier",
      "Invalid student identifier supplied.",
    );
  }

  try {
    return await getStudentByUid(normalizedIdentifier);
  } catch (error) {
    if (
      !(error instanceof StudentQrError) ||
      error.code !== "student-not-found"
    ) {
      throw error;
    }
  }

  const documentSnapshot = await getDoc(
    doc(db, STUDENTS_COLLECTION, rawIdentifier),
  );

  if (documentSnapshot.exists()) {
    return mapStudentDocument(documentSnapshot);
  }

  if (rawIdentifier !== normalizedIdentifier) {
    const normalizedDocumentSnapshot = await getDoc(
      doc(db, STUDENTS_COLLECTION, normalizedIdentifier),
    );

    if (normalizedDocumentSnapshot.exists()) {
      return mapStudentDocument(normalizedDocumentSnapshot);
    }
  }

  throw new StudentQrError("student-not-found", "Student not found", {
    uid: normalizedIdentifier,
  });
}

export async function updateStudentQr({ studentId, studentName, qrImageUrl }) {
  if (!studentId) {
    throw new StudentQrError(
      "missing-student-id",
      "Cannot update a student without a Firestore document ID.",
    );
  }

  const activeqr = shouldActivateQrForStudentName(studentName);

  await updateDoc(doc(db, STUDENTS_COLLECTION, studentId), {
    qrImageUrl,
    activeqr,
  });

  return { activeqr, qrImageUrl };
}

export async function clearStudentQr({ studentId }) {
  if (!studentId) {
    throw new StudentQrError(
      "missing-student-id",
      "Cannot delete a student QR without a Firestore document ID.",
    );
  }

  await updateDoc(doc(db, STUDENTS_COLLECTION, studentId), {
    qrImageUrl: deleteField(),
    activeqr: false,
  });

  return { activeqr: false };
}
